import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { base44 } from '@/api/base44Client';
import { motion } from 'framer-motion';
import { format } from 'date-fns';
import {
  Loader2, CreditCard, ShieldCheck, CheckCircle, AlertTriangle, Calendar, Clock, Users, Cake,
} from 'lucide-react';
import SquareApplePayButton, { loadSquareSdk, squareVerifyBuyer } from '@/components/checkout/SquareApplePayButton';
import SquareGooglePayButton from '@/components/checkout/SquareGooglePayButton';
import DriverCountSelector from '@/components/groupbookings/DriverCountSelector';

const SQUARE_APP_ID = import.meta.env.VITE_SQUARE_APP_ID || 'sq0idp-HblpXluK_lgmGh2wdXzdCA';
const SQUARE_LOCATION_ID = import.meta.env.VITE_SQUARE_LOCATION_ID || 'LHHYN7QJW6P94';

// Public, token-gated balance payment page (/party-balance/:token).
//
// Loads the party deposit Order via getPartyBalanceOrder (validates the
// secure token, no login required), lets the customer confirm their final
// driver headcount (4–8), recalculates the balance (headcount × per-driver
// price − £50 deposit), and charges the balance via the Square Web Payments
// SDK (card / Apple Pay / Google Pay).

export default function PartyBalance() {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [order, setOrder] = useState(null);
  const [headcount, setHeadcount] = useState(4);
  const [card, setCard] = useState(null);
  const [paying, setPaying] = useState(false);
  const [payError, setPayError] = useState('');
  const [success, setSuccess] = useState(false);

  // Load order by token
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await base44.functions.invoke('getPartyBalanceOrder', { token });
        if (cancelled) return;
        if (res.data?.error) {
          setError(res.data.error);
        } else if (res.data?.alreadyPaid) {
          setOrder({ alreadyPaid: true });
        } else {
          setOrder(res.data);
          setHeadcount(Number(res.data.estimated_headcount) || 4);
        }
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Unable to load this balance link.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [token]);

  // Init Square card form once order loaded
  useEffect(() => {
    if (!order || order.alreadyPaid) return;
    let destroyed = false;
    const initSquare = async () => {
      try {
        await loadSquareSdk();
        if (destroyed || !window.Square) return;
        const payments = window.Square.payments(SQUARE_APP_ID, SQUARE_LOCATION_ID);
        const cardInstance = await payments.card({
          style: {
            '.input-container': { borderColor: 'rgba(255,255,255,0.1)', borderRadius: '0px' },
            input: { color: '#fff', backgroundColor: '#111115', fontSize: '14px' },
            'input::placeholder': { color: 'rgba(255,255,255,0.2)' },
            '.message-text': { color: 'rgba(255,255,255,0.4)' },
          },
        });
        if (destroyed) { try { cardInstance.destroy(); } catch {} return; }
        await cardInstance.attach('#sq-balance-card');
        if (!destroyed) setCard(cardInstance);
      } catch (e) {
        console.warn('[PartyBalance] Square init failed:', e?.message);
      }
    };
    initSquare();
    return () => {
      destroyed = true;
      setCard((c) => { if (c) { try { c.destroy(); } catch {} } return null; });
    };
  }, [order]);

  const perDriver = Number(order?.per_driver_price) || 0;
  const deposit = Number(order?.deposit_amount) || 50;
  const total = headcount * perDriver;
  const balanceDue = Math.max(0, total - deposit);

  const buildVerificationDetails = () => ({
    amount: balanceDue.toFixed(2),
    currencyCode: 'GBP',
    intent: 'CHARGE',
    billingContact: {
      givenName: order?.first_name || 'Customer',
      email: order?.contact_email || undefined,
      countryCode: 'GB',
    },
  });

  const charge = async (sourceId, verificationToken) => {
    return await base44.functions.invoke('partyBalanceCheckout', {
      token,
      sourceId,
      verificationToken,
      final_headcount: headcount,
    });
  };

  const handleCardPay = async (e) => {
    e.preventDefault();
    if (!card) { setPayError('Card form not ready. Please wait.'); return; }
    setPaying(true);
    setPayError('');
    try {
      const result = await card.tokenize();
      if (result.status !== 'OK') {
        setPayError(result.errors?.[0]?.message || 'Card error. Please check your details.');
        setPaying(false);
        return;
      }
      let verifiedToken;
      try {
        verifiedToken = await squareVerifyBuyer(result.token, buildVerificationDetails());
      } catch (err) {
        setPayError(err?.message || '3D Secure verification failed. Please try again.');
        setPaying(false);
        return;
      }
      const res = await charge(result.token, verifiedToken);
      if (res.data?.success) {
        setSuccess(true);
      } else {
        setPayError(res.data?.error || 'Payment failed. Please try again.');
      }
    } catch (err) {
      setPayError(err?.message || 'Payment failed. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  const handleWalletToken = async (tokenized) => {
    setPaying(true);
    setPayError('');
    try {
      let verifiedToken;
      try {
        verifiedToken = await squareVerifyBuyer(tokenized, buildVerificationDetails());
      } catch (err) {
        setPayError(err?.message || 'Payment verification failed. Please try again.');
        setPaying(false);
        return;
      }
      const res = await charge(tokenized, verifiedToken);
      if (res.data?.success) {
        setSuccess(true);
      } else {
        setPayError(res.data?.error || 'Payment failed. Please try again.');
      }
    } catch (err) {
      setPayError(err?.message || 'Payment failed. Please try again.');
    } finally {
      setPaying(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <AlertTriangle className="w-12 h-12 text-primary mx-auto mb-4" />
          <h1 className="text-white font-black text-xl uppercase mb-2">Link Invalid</h1>
          <p className="text-white/50 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (order?.alreadyPaid) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] flex items-center justify-center p-6">
        <div className="max-w-md text-center">
          <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-4" />
          <h1 className="text-white font-black text-xl uppercase mb-2">Balance Already Paid</h1>
          <p className="text-white/50 text-sm">Your party balance has already been settled. See you at the track!</p>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-[#0d0d0f] flex items-center justify-center p-6">
        <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="max-w-md text-center">
          <CheckCircle className="w-14 h-14 text-primary mx-auto mb-5" />
          <h1 className="text-white font-black text-2xl uppercase mb-3">Balance Paid</h1>
          <p className="text-white/50 text-sm mb-6 leading-relaxed">
            Your balance of <span className="text-white font-bold">£{balanceDue.toFixed(2)}</span> for {headcount} drivers has been received. A confirmation email is on its way — we can't wait to see you on the day!
          </p>
          <div className="inline-flex flex-wrap justify-center gap-3 text-xs text-white/40">
            <span>Package: <span className="text-white/60">{order?.package_name}</span></span>
          </div>
        </motion.div>
      </div>
    );
  }

  const labelClass = 'block text-white/30 text-[10px] font-bold tracking-widest mb-1.5';

  return (
    <div className="min-h-screen bg-[#0d0d0f] py-10 px-4">
      <div className="max-w-xl mx-auto">
        {/* Header */}
        <div className="text-center mb-8">
          <p className="text-primary text-[11px] font-bold tracking-[0.3em] mb-2">PARTY BALANCE PAYMENT</p>
          <h1 className="text-white font-black text-2xl uppercase">Confirm Headcount & Pay</h1>
        </div>

        {/* Event summary */}
        <div className="border border-white/5 bg-[#111115] p-5 mb-5">
          <p className="text-primary text-[10px] font-bold tracking-widest mb-3">EVENT DETAILS</p>
          <div className="grid grid-cols-2 gap-3 text-xs">
            <div className="flex items-center gap-2">
              <Calendar className="w-3.5 h-3.5 text-white/30" />
              <span className="text-white/70">{order?.event_date ? format(new Date(order.event_date + 'T00:00:00'), 'dd MMM yyyy') : '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Clock className="w-3.5 h-3.5 text-white/30" />
              <span className="text-white/70">{order?.event_time || '—'}</span>
            </div>
            <div className="flex items-center gap-2">
              <Users className="w-3.5 h-3.5 text-white/30" />
              <span className="text-white/70">{order?.package_name}</span>
            </div>
            {order?.birthday_child_name && (
              <div className="flex items-center gap-2">
                <Cake className="w-3.5 h-3.5 text-white/30" />
                <span className="text-white/70">{order.birthday_child_name}{order?.birthday_child_age ? ` (age ${order.birthday_child_age})` : ''}</span>
              </div>
            )}
          </div>
        </div>

        {/* Headcount selector */}
        <div className="border border-white/5 bg-[#111115] p-5 mb-5">
          <DriverCountSelector value={headcount} onChange={setHeadcount} min={4} max={8} />
          <div className="grid grid-cols-3 gap-3 mt-5">
            <div>
              <p className="text-white/30 text-[9px] font-bold tracking-widest">TOTAL</p>
              <p className="text-white font-black text-lg mt-0.5">£{total.toFixed(2)}</p>
              <p className="text-white/30 text-[10px]">{headcount} × £{perDriver}</p>
            </div>
            <div>
              <p className="text-white/30 text-[9px] font-bold tracking-widest">DEPOSIT</p>
              <p className="text-white/60 font-black text-lg mt-0.5">−£{deposit.toFixed(2)}</p>
              <p className="text-white/30 text-[10px]">already paid</p>
            </div>
            <div>
              <p className="text-primary text-[9px] font-bold tracking-widest">BALANCE DUE</p>
              <p className="text-primary font-black text-lg mt-0.5">£{balanceDue.toFixed(2)}</p>
              <p className="text-white/30 text-[10px]">pay now</p>
            </div>
          </div>
        </div>

        {/* Payment */}
        <div className="border border-white/5 bg-[#111115] p-5">
          <p className="text-primary text-[10px] font-bold tracking-widest mb-4">PAY BALANCE</p>

          {/* Apple Pay / Google Pay */}
          <div className="space-y-2 mb-4">
            <SquareApplePayButton
              amount={balanceDue}
              label="Motion Reality Party Balance"
              onToken={handleWalletToken}
              onError={setPayError}
              disabled={paying}
              containerId="sq-balance-applepay"
            />
            <SquareGooglePayButton
              amount={balanceDue}
              label="Motion Reality Party Balance"
              onToken={handleWalletToken}
              onError={setPayError}
              disabled={paying}
              containerId="sq-balance-googlepay"
            />
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="flex-1 h-px bg-white/5" />
            <span className="text-white/20 text-[10px] font-bold tracking-widest">OR PAY BY CARD</span>
            <div className="flex-1 h-px bg-white/5" />
          </div>

          <form onSubmit={handleCardPay} className="space-y-4">
            <div id="sq-balance-card" className="min-h-[44px]" />

            {payError && <p className="text-red-400 text-xs font-medium">{payError}</p>}

            <button
              type="submit"
              disabled={paying || !card || balanceDue <= 0}
              className="w-full inline-flex items-center justify-center gap-2 bg-primary hover:bg-primary/90 text-white font-bold text-sm tracking-widest py-3.5 transition-colors disabled:opacity-50"
            >
              {paying ? <Loader2 className="w-4 h-4 animate-spin" /> : <CreditCard className="w-4 h-4" />}
              PAY £{balanceDue.toFixed(2)} BALANCE
            </button>
          </form>

          <div className="flex items-center justify-center gap-1.5 text-white/30 text-[10px] mt-3">
            <ShieldCheck className="w-3 h-3" />
            Secure payment via Square. We never store your card details.
          </div>
        </div>
      </div>
    </div>
  );
}